import type { SkinId } from "./skins.js";
import type { ElementDessin, PlanDessin } from "../plan/dessin.js";
import { Barre, Bulle, Ecran, Etape, Retour, heure } from "./ui.js";
import { EtapesAVenir } from "./ecrans2.js";
import { ESPACES_HUMAINS } from "../espaces.js";

/**
 * L'entrée d'un événement (B2B) : on y arrive par sa position, dans la zone
 * et pendant les horaires. L'organisateur parle à la place de Hoa (son nom,
 * son image) ; il dit son texte d'accueil, prévient si les échanges sont
 * gardés, et montre ses tables.
 */

export interface TablePublique {
  sessionId: string;
  nom: string;
  sujet: string;
  point: string;
  repere: string;
  rdvAt: number;
  couleur: string;
  participants: number;
  maximum: number;
  phase: string;
}

export interface EvenementPublic {
  id: string;
  nom: string;
  lieu: string;
  fin: number;
  voix: { nom: string; image: string | null; phrase: string; couleur?: string | null } | null;
  accueil: string;
  donnees: string | null;
  tables: TablePublique[];
  /** Le plan du stand (régie), ou `null` s'il n'a pas été dessiné. */
  plan?: PlanPublic | null;
}

export interface PlanPublic extends PlanDessin {
  elements: Array<ElementDessin & { sessionId: string | null }>;
}

export function EcranEvenement(props: {
  skin: SkinId;
  evenement: EvenementPublic;
  autres: EvenementPublic[];
  maTable: string | null;
  dessus?: React.ReactNode;
  onChoisir: (sessionId: string) => void;
  onAutre: (id: string) => void;
  onVille: () => void;
  onRetour: () => void;
}): JSX.Element {
  const e = props.evenement;
  const tables = e.tables.slice().sort((a, b) => a.rdvAt - b.rdvAt || a.nom.localeCompare(b.nom, "fr", { numeric: true }));
  return (
    <Ecran skin={props.skin} label={e.nom} dessus={props.dessus}>
      <Barre>
        {/* Espaces Humains : on est entré par le QR du stand, il n'y a rien « avant ». */}
        {ESPACES_HUMAINS ? null : (
          <button type="button" className="hoa-rond" aria-label="Retour" onClick={props.onRetour}>
            <Retour />
          </button>
        )}
        <span className="titre">{e.nom}</span>
      </Barre>
      <div className="hoa-frise">
        <Etape etat="courante" expression="sourire">
          <Bulle>
            {e.voix ? <small className="hoa-voix-nom">{e.voix.nom}</small> : null}
            {e.voix?.phrase ? (
              <>
                {e.voix.phrase}
                <br />
              </>
            ) : null}
            {e.accueil}
          </Bulle>
          {e.donnees ? (
            <p className="hoa-note" style={{ marginTop: 10 }}>
              {e.donnees}
            </p>
          ) : null}
          <div className="hoa-liste" style={{ marginTop: 12 }}>
            {tables.length === 0 ? <p className="hoa-note">Les tables ne sont pas encore ouvertes. Reviens dans un instant.</p> : null}
            {tables.map((t) => {
              const pleine = t.participants >= t.maximum;
              const mienne = props.maTable === t.sessionId;
              return (
                <button
                  key={t.sessionId}
                  type="button"
                  className={mienne ? "hoa-ligne active" : "hoa-ligne"}
                  disabled={pleine && !mienne}
                  onClick={() => props.onChoisir(t.sessionId)}
                >
                  <i className="hoa-pastille" style={{ background: t.couleur }} aria-hidden="true" />
                  <div>
                    <b>
                      {t.nom}
                      {t.sujet && t.sujet !== t.nom ? ` · ${t.sujet}` : ""}
                    </b>
                    <span>
                      {mienne ? "Ta table · y revenir" : `${t.point ? `${t.point}, ` : ""}${heure(t.rdvAt)}`}
                    </span>
                  </div>
                  <em aria-label={`${t.participants} sur ${t.maximum}`}>{pleine ? "complet" : `${t.participants}/${t.maximum}`}</em>
                </button>
              );
            })}
            {props.autres
              .filter((x) => x.id !== e.id)
              .map((x) => (
                <button key={x.id} type="button" className="hoa-ligne nouvelle" onClick={() => props.onAutre(x.id)}>
                  {x.nom}
                </button>
              ))}
          </div>
          {ESPACES_HUMAINS ? null : (
            <button type="button" className="hoa-lien" style={{ marginTop: 12 }} onClick={props.onVille}>
              Voir les tribus de la ville
            </button>
          )}
        </Etape>
        <EtapesAVenir table />
      </div>
    </Ecran>
  );
}

/** Espaces Humains, sans événement ouvert : le seul chemin est le QR code du stand. */
export function EcranSansEvenement(props: { skin: SkinId; dessus?: React.ReactNode }): JSX.Element {
  return (
    <Ecran skin={props.skin} label="Espaces Humains" dessus={props.dessus}>
      <Barre>
        <span className="titre">Espaces Humains</span>
      </Barre>
      <div className="hoa-frise">
        <Etape etat="courante" expression="sourire" dernier>
          <Bulle>Scannez le QR code du stand pour rejoindre une table.</Bulle>
        </Etape>
      </div>
    </Ecran>
  );
}
